import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { PlusIcon, SparklesIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useBlockEditor } from '../../hooks';
import { Block, ChiffrageView, isPrestation } from '../../types';
import { SuggestionPrestation } from '../../types/SuggestionTypes';
import { BlockList } from './BlockList';
import { suggestionService, DecompositionParams, BlockCalculationService } from '../../services';

interface BlockEditorProps {
  initialBlocks?: Block[];
  onBlocksChange?: (blocks: Block[]) => void;
  onSelectedBlocksChange?: (selectedBlocks: Block[]) => void;
  onSelectAll?: React.MutableRefObject<(() => void) | null>;
  onClearSelection?: React.MutableRefObject<(() => void) | null>;
  isModalOpen?: boolean;
  className?: string;
  consultationMap?: Map<string, { name: string; id: string; status?: string; retainedCompanyName?: string }>;
  onOpenConsultation?: (consultationId: string) => void;
  events?: import('../../types/Version').VersionEvent[];
  onPointEvent?: (eventId: string, lotId: string) => void;
  onOpenCreateEvent?: () => void;
  chiffrageView?: ChiffrageView;
  onChiffrageViewChange?: (view: ChiffrageView) => void;
  decompositionParams?: DecompositionParams;
  /** Lot actif (onglet TOC) — cible de l'ajout depuis "usage défini" / suggestion. */
  activeLotId?: string;
}

const BlockEditor: React.FC<BlockEditorProps> = ({
  initialBlocks = [],
  onBlocksChange,
  onSelectedBlocksChange,
  onSelectAll,
  onClearSelection,
  isModalOpen = false,
  className = '',
  consultationMap,
  onOpenConsultation,
  events,
  onPointEvent,
  onOpenCreateEvent,
  chiffrageView = 'client',
  onChiffrageViewChange,
  decompositionParams,
  activeLotId,
}) => {
  const {
    state,
    actions,
    selection,
    dragDrop,
    selectBlock,
    toggleSelection,
    selectRange,
    selectAllBlocks,
    clearSelection,
    editBlock,
    stopEditing,
    getBlocksInOrder,
    reorderBlocks,
    syncLevels,
    syncWithExternalBlocks,
  } = useBlockEditor(initialBlocks);

  const editorRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<SuggestionPrestation[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Ventilation accordion state (par ligne uniquement — plus de barre globale)
  const [ventilationExpandedIds, setVentilationExpandedIds] = useState<Set<string>>(new Set());

  const handleToggleVentilation = useCallback((id: string) => {
    setVentilationExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Décomposition F/MO : accordéon par ligne (indépendant de la vue globale)
  const [decompositionExpandedIds, setDecompositionExpandedIds] = useState<Set<string>>(new Set());

  const handleToggleDecomposition = useCallback((id: string) => {
    setDecompositionExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleVentilationBlocksChange = useCallback((newBlocks: Block[]) => {
    syncWithExternalBlocks(newBlocks);
  }, [syncWithExternalBlocks]);

  // Notifier les changements de blocs — on envoie TOUS les blocs (y compris
  // les enfants des sections fermées) pour ne pas les perdre dans le state parent.
  // Ref pour éviter la boucle quand le parent passe un onBlocksChange non-mémoïsé
  // (ex: handleBlocksChange dépendant de activeLotSubtreeIds qui change à chaque
  // mutation de blocks) : on ne redispatch qu'à chaque vrai changement de state.blocks.
  const onBlocksChangeRef = useRef(onBlocksChange);
  useEffect(() => { onBlocksChangeRef.current = onBlocksChange; }, [onBlocksChange]);
  useEffect(() => {
    onBlocksChangeRef.current?.(Object.values(state.blocks));
  }, [state.blocks]);

  // Synchroniser les niveaux après les opérations de drag & drop
  useEffect(() => {
    if (dragDrop.isDragging === false) {
      // Délai pour permettre aux mises à jour d'état de se terminer
      const timeoutId = setTimeout(() => {
        syncLevels();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [dragDrop.isDragging, syncLevels]);

  // Notifier les changements de sélection — on dépend uniquement de l'état
  // sélection (Set stable entre les mutations réelles) et des blocs. Dépendre
  // de `selection` (objet littéral recréé à chaque render du hook) provoquait
  // une boucle infinie (setState dans parent → re-render → selection=nouveau
  // ref → useEffect refire → setState → ...).
  const onSelectedBlocksChangeRef = useRef(onSelectedBlocksChange);
  useEffect(() => { onSelectedBlocksChangeRef.current = onSelectedBlocksChange; }, [onSelectedBlocksChange]);
  useEffect(() => {
    if (!onSelectedBlocksChangeRef.current) return;
    const selectedBlocksArray = selection.getSelectedBlocks(getBlocksInOrder());
    onSelectedBlocksChangeRef.current(selectedBlocksArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.selectedBlocks, state.blocks]);

  // Exposer les fonctions de sélection globale via useImperativeHandle ou callback
  const handleSelectAll = useCallback(() => {
    const allBlocks = getBlocksInOrder();
    selectAllBlocks(allBlocks);
  }, [selectAllBlocks, getBlocksInOrder]);

  const handleClearSelection = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // Exposer les fonctions via les refs
  useEffect(() => {
    if (onSelectAll) {
      onSelectAll.current = handleSelectAll;
    }
  }, [onSelectAll, handleSelectAll]);

  useEffect(() => {
    if (onClearSelection) {
      onClearSelection.current = handleClearSelection;
    }
  }, [onClearSelection, handleClearSelection]);


  // Toggle collapse d'une section
  const handleToggleCollapse = useCallback((id: string) => {
    const block = state.blocks[id];
    if (block) {
      actions.updateBlock(id, { isCollapsed: !block.isCollapsed });
    }
  }, [state.blocks, actions]);

  // Gestion de l'ajout de blocs — cible le lot actif si fourni, sinon racine.
  const handleAddPrestation = useCallback(() => {
    const newBlockId = actions.addPrestation(activeLotId);
    selection.selectBlock(newBlockId);
    editBlock(newBlockId);

    setTimeout(() => {
      stopEditing();
    }, 100);
  }, [actions, selection, editBlock, stopEditing, activeLotId]);

  // Gestion de l'ajout d'une prestation suggérée — cible le lot actif si fourni.
  const handleAddSuggestion = useCallback((suggestion: SuggestionPrestation) => {
    const newBlockId = actions.addPrestation(activeLotId);

    actions.updateBlock(newBlockId, {
      content: {
        titre: suggestion.intitule,
        intitule: suggestion.intitule,
        categorie: suggestion.categorie,
        unite: suggestion.unite,
        prixUnitaire: suggestion.prixUnitaire,
        quantite: suggestion.quantite,
        prixTotal: suggestion.prixTotal,
        chantier: suggestion.chantier,
        fournisseur: suggestion.fournisseur
      }
    });

    selection.selectBlock(newBlockId);
  }, [actions, selection, activeLotId]);

  // Générer des suggestions (contextual — passe les blocs actuels)
  const generateSuggestions = useCallback(async (currentBlocks: Block[]) => {
    setIsLoadingSuggestions(true);
    try {
      const newSuggestions = await suggestionService.generateSuggestions(currentBlocks);
      setSuggestions(newSuggestions);
    } catch (error) {
      console.error('Erreur lors de la génération des suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, []);

  // Regénère les suggestions quand les blocs changent (debounce 600ms)
  useEffect(() => {
    const id = setTimeout(() => generateSuggestions(Object.values(state.blocks)), 600);
    return () => clearTimeout(id);
  }, [state.blocks, generateSuggestions]);

  // Gestion des événements de souris globaux
  useEffect(() => {
    const handleGlobalMouseDown = (e: MouseEvent) => {
      if (isModalOpen) return;
      
      const editorRect = editorRef.current?.getBoundingClientRect();
      const isInEditor = editorRect ? 
        e.clientX >= editorRect.left && e.clientX <= editorRect.right &&
        e.clientY >= editorRect.top && e.clientY <= editorRect.bottom : false;
      
      const target = e.target as HTMLElement;
      const isInteractiveElement = target.closest('button, input, [role="button"], [data-drag-handle]');
      const blockElement = target.closest('[data-block-id]');
      const blockId = blockElement?.getAttribute('data-block-id');
      
      // Suppression de la sélection par clic - seules les checkboxes permettent la sélection
      // On garde seulement la sélection par zone pour les cas spéciaux
      if (isInEditor && !isInteractiveElement && !blockId) {
        const isAdditiveSelection = e.ctrlKey || e.metaKey;
        selection.startAreaSelection(e.clientX, e.clientY, isAdditiveSelection);
      }
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (selection.isSelecting && selection.selectionStart) {
        selection.updateAreaSelection(e.clientX, e.clientY);
      }
      
      if (dragDrop.isDragging && editorRef.current) {
        const editorRect = editorRef.current.getBoundingClientRect();
        dragDrop.updateDragPosition(e, editorRect, getBlocksInOrder(), state.rootBlocks);
      }
    };

    const handleGlobalMouseUp = () => {
      if (selection.isSelecting) {
        selection.endAreaSelection();
      }
      
      if (dragDrop.isDragging) {
        if (dragDrop.dropTarget) {
          const { index, parentId, level } = dragDrop.dropTarget;
          let targetIndex = index;
          
          if (dragDrop.dropTarget.position === 'below') {
            targetIndex = index + 1;
          }
          
          reorderBlocks(dragDrop.draggedBlocks, targetIndex, parentId, level);
        }
        
        dragDrop.endDrag();
      }
    };

    document.addEventListener('mousedown', handleGlobalMouseDown);
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [
    isModalOpen,
    selection,
    dragDrop,
    getBlocksInOrder,
    state.rootBlocks,
    reorderBlocks
  ]);

  // Gestion des événements clavier
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (isModalOpen) return;

    // Ne pas intercepter les touches quand le focus est sur un champ de saisie
    const target = e.target as HTMLElement;
    const isEditableTarget =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable;

    if (e.key === 'Escape') {
      e.preventDefault();
      stopEditing();
      selection.clearSelection();
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (isEditableTarget) return; // laisser l'input gérer sa propre suppression
      e.preventDefault();

      if (selection.selectedBlocks.size > 0) {
        selection.selectedBlocks.forEach(blockId => {
          actions.deleteBlock(blockId);
        });
        selection.clearSelection();
      } else if (state.selectedBlockId) {
        actions.deleteBlock(state.selectedBlockId);
        selection.clearSelection();
      }
    }
  }, [isModalOpen, stopEditing, selection, actions, state.selectedBlockId]);

  const blocksInOrder = useMemo(() => {
    // Les lignes ventilées vivent exclusivement dans le panneau de ventilation
    // (accordéon par prestation) — jamais dans la liste principale, peu importe
    // la vue. Les afficher ici créait un doublon visuel.
    return getBlocksInOrder().filter(b => !b.content.isVentilationCostLine);
  }, [getBlocksInOrder]);

  // Total HT de tous les blocs visibles — somme des totaux de niveau racine
  // (sections ou prestations), calculés récursivement en excluant les lignes
  // ventilées pour éviter le double comptage. Se met à jour automatiquement
  // dès qu'un PU, une quantité ou un coef change.
  const totalHT = useMemo(() => {
    const allBlocksArr = Object.values(state.blocks);
    const visibleAllBlocks = allBlocksArr.filter(b => !b.content.isVentilationCostLine);
    return state.rootBlocks
      .map(id => state.blocks[id])
      .filter((b): b is Block => !!b)
      .reduce((sum, block) => {
        // Pour les prestations racines, on prend leur total ; pour les sections
        // racines, la somme récursive de leurs enfants (déjà gérée par le service).
        return sum + (
          isPrestation(block)
            ? BlockCalculationService.calculatePrestationTotal(block)
            : BlockCalculationService.calculatePrestationTotal(block, visibleAllBlocks)
        );
      }, 0);
  }, [state.blocks, state.rootBlocks]);

  return (
    <div className={`block-editor ${className}`}>
      <div
        ref={editorRef}
        className="min-h-screen bg-white pb-6 focus:outline-none relative"
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* Zone de création initiale si aucun bloc */}
        {state.rootBlocks.length === 0 && (
          <div className="flex items-center justify-center min-h-[200px] border-2 border-dashed border-gray-200 rounded-lg">
            <button
              onClick={handleAddPrestation}
              className="flex items-center space-x-2 px-4 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Commencer un chiffrage</span>
            </button>
          </div>
        )}

        {/* Zone de sélection visuelle */}
        {selection.isSelecting && selection.selectionStart && selection.selectionEnd && (
          <div
            className="absolute border-2 border-blue-400 bg-blue-100 bg-opacity-20 pointer-events-none z-10"
            style={{
              left: Math.min(selection.selectionStart.x, selection.selectionEnd.x) - (editorRef.current?.getBoundingClientRect().left || 0),
              top: Math.min(selection.selectionStart.y, selection.selectionEnd.y) - (editorRef.current?.getBoundingClientRect().top || 0),
              width: Math.abs(selection.selectionEnd.x - selection.selectionStart.x),
              height: Math.abs(selection.selectionEnd.y - selection.selectionStart.y),
            }}
          />
        )}

        {/* Indicateur de drop */}
        {dragDrop.isDragging && dragDrop.dropTarget && (
          <div
            className="absolute h-0.5 bg-blue-500 pointer-events-none z-20"
            style={{
              left: (dragDrop.dropTarget.level || 0) * 20 + 20,
              right: 0,
              top: dragDrop.dropTarget.top,
            }}
          />
        )}

        {/* Barre supérieure : Vue + Total HT groupés à droite (aligné avec la colonne total des lignes).
            La vue filtre ventilation + décomposition F/MO :
              - détaillée : tout visible (ventilation + F/MO)
              - ventilée  : ventilation visible, F/MO masqué
              - client    : ventilation + F/MO masqués (identique à l'export) */}
        {state.rootBlocks.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-gray-100 bg-gray-50/50">
            {onChiffrageViewChange && (
              <div className="flex items-center gap-2">
                <EyeIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[11px] text-gray-400 uppercase tracking-wider">Vue</span>
                <div className="inline-flex rounded-md border border-gray-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => onChiffrageViewChange('detaillee')}
                    className={`px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      chiffrageView === 'detaillee'
                        ? 'bg-violet-600 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    title="Vue détaillée — affiche tout : ventilation + décomposition F/MO"
                  >
                    Détaillée
                  </button>
                  <button
                    type="button"
                    onClick={() => onChiffrageViewChange('ventilee')}
                    className={`px-2.5 py-0.5 text-xs font-medium transition-colors border-l border-gray-200 ${
                      chiffrageView === 'ventilee'
                        ? 'bg-violet-600 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    title="Vue ventilée — ventilation visible, décomposition F/MO masquée"
                  >
                    Ventilée
                  </button>
                  <button
                    type="button"
                    onClick={() => onChiffrageViewChange('client')}
                    className={`px-2.5 py-0.5 text-xs font-medium transition-colors border-l border-gray-200 ${
                      chiffrageView === 'client'
                        ? 'bg-violet-600 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    title="Vue client — ventilation et décomposition F/MO masquées (identique à l'export)"
                  >
                    Client
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] text-gray-400 uppercase tracking-wider">Total HT</span>
              <span className="text-sm font-semibold text-violet-600 tabular-nums">
                {BlockCalculationService.formatPrice(totalHT)} €
              </span>
            </div>
          </div>
        )}

        {/* Rendu des blocs optimisé */}
        <BlockList
          blocks={blocksInOrder}
          allBlocks={Object.values(state.blocks)}
          selectedBlocks={selection.selectedBlocks}
          editingBlockId={state.editingBlockId}
          draggedBlocks={dragDrop.draggedBlocks}
          onSelect={selectBlock}
          onEdit={editBlock}
          onUpdate={actions.updateBlock}
          onDelete={actions.deleteBlock}
          onDragStart={(id, e) => dragDrop.startDrag(id, selection.selectedBlocks, e)}
          onDragEnd={dragDrop.endDrag}
          onStopEditing={stopEditing}
          onToggleSelection={toggleSelection}
          onRangeSelect={selectRange}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          isBlockDragging={dragDrop.isBlockDragging}
          onToggleCollapse={handleToggleCollapse}
          consultationMap={consultationMap}
          onOpenConsultation={onOpenConsultation}
          ventilationExpandedIds={ventilationExpandedIds}
          onToggleVentilation={handleToggleVentilation}
          onVentilationBlocksChange={handleVentilationBlocksChange}
          decompositionExpandedIds={decompositionExpandedIds}
          onToggleDecomposition={handleToggleDecomposition}
          decompositionParams={decompositionParams}
          chiffrageView={chiffrageView}
          events={events}
          onPointEvent={onPointEvent}
          onOpenCreateEvent={onOpenCreateEvent}
        />

        {/* Zone bas — Ajouter + suggestions sur une seule ligne */}
        {state.rootBlocks.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={handleAddPrestation}
              className="group flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-violet-700 hover:bg-violet-50 border border-dashed border-gray-200 hover:border-violet-300 rounded-lg transition-all duration-150"
            >
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-100 group-hover:bg-violet-100 transition-colors">
                <PlusIcon className="w-2.5 h-2.5" />
              </span>
              Ajouter une ligne
            </button>

            {isLoadingSuggestions ? (
              <span className="text-xs text-gray-300 animate-pulse">…</span>
            ) : suggestions.length > 0 && (
              <>
                <span className="text-gray-200 select-none">|</span>
                {suggestions.slice(0, 3).map((s, i) => (
                  <React.Fragment key={s.id}>
                    {i > 0 && <span className="text-gray-200 text-xs select-none">·</span>}
                    <button
                      onClick={() => handleAddSuggestion(s)}
                      className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-violet-600 transition-colors"
                    >
                      <SparklesIcon className="w-3 h-3 text-violet-300" />
                      {s.intitule}
                    </button>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockEditor;